import * as React from 'react';
import { send_authorized_request } from '../Constants/cognito'
import { AXIOS_API_INSTANCE } from '../Constants';
import { useSnackbar } from 'notistack';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import Grid from "@mui/material/Grid2"
import { MuiColorInput } from 'mui-color-input'
import { LoadingBackdropIndeterminate } from './LoadingBackdrop';
import ClientConfigurationStatuses from './ClientConfigurationStatuses';
import { useTrackedTheme, useTrackedUser } from './UserState';
import { createTheme } from '@mui/material/styles';
import { useUsers } from './ReactQuery/QueryUsers';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from 'axios';
import DocumentTitle from '../Utils/DocumentTitle';
import ClientConfigurationPartsStatuses from './ClientConfigurationPartsStatuses';

export default function ClientConfiguration() {
    DocumentTitle('Client Configuration')
    const { enqueueSnackbar } = useSnackbar();
    const [theme, setTheme] = useTrackedTheme();
    const [my_user, set_my_user] = useTrackedUser();
    const { data: users } = useUsers();
    const [client, setClient] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(false)
    const [isLoadingAITokens, setIsLoadingAITokens] = React.useState(false)
    const [AITokensUsed, setAITokensUsed] = React.useState(0)
    const [companyColor, setCompanyColor] = React.useState(client.client_color || "")
    const [previousCompanyColor, setPreviousCompanyColor] = React.useState(client.client_color || "")


    const handleChange = (prop) => (event) => {
        setClient({ ...client, [prop]: event.target.value })
    }

    const save = () => {
        setIsLoading(true)
        delete client.date_created
        delete client.date_updated
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`client/update`, client).then((client) => {
                enqueueSnackbar('Successfully updated client', { variant: 'success' })
                setIsLoading(false)
                setClient(client.data.data)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to update client <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const getClient = () => {
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("client").then(
                (client) => {
                    setClient(client.data.data)
                    setCompanyColor(client.data.data.client_color)
                    setPreviousCompanyColor(client.data.data.client_color)
                    setIsLoading(false)
                }
            ).catch((error) => {
                setIsLoading(false)
                console.log(error);
                enqueueSnackbar(`${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
            }))
    }

    const getAITokensUsed = () => {
        setIsLoadingAITokens(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("ai/tokens_used_this_month").then((tokens) => {
                setIsLoadingAITokens(false)
                setAITokensUsed(tokens.data.data)
            }
            ).catch((error) => {
                setIsLoadingAITokens(false)
                console.log(error);
                enqueueSnackbar(`Could not get AI Tokens used: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
            }))
    }

    const previewCompanyColor = () => {
        setTheme({
            ...createTheme(theme, {
                palette: {
                    brandcolor: theme.palette.augmentColor({
                        color: {
                            'main': companyColor,
                        },
                        name: 'brandcolor'
                    })
                }
            })
        })
    }

    const resetCompanyColor = () => {
        setTheme({
            ...createTheme(theme, {
                palette: {
                    brandcolor: theme.palette.augmentColor({
                        color: {
                            'main': previousCompanyColor,
                        },
                        name: 'brandcolor'
                    })
                }
            })
        })
        setCompanyColor(previousCompanyColor)
        setClient({ ...client, client_color: previousCompanyColor })
    }

    const uploadNewLogo = (event) => {
        if (!event.target.files || event.target.files.length === 0) {
            return;
        }
        const file = event.target.files[0];
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`client/image`, { image_name: file.name },).then((response) => {
                console.log(response)
                var bodyFormData = new FormData();
                for (const key in response.data.data.fields) {
                    bodyFormData.append(key, response.data.data.fields[key])
                }
                bodyFormData.append('file', file)
                axios.post(response.data.data.url, bodyFormData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((s3_response) => {
                    console.log(s3_response)
                    setIsLoading(false)
                    enqueueSnackbar('Successfully uploaded logo', { variant: 'success' })
                    setClient(response.data.client)
                    set_my_user({ ...my_user, client: response.data.client })
                }).catch((error) => {
                    setIsLoading(false)
                    enqueueSnackbar(`Failed to upload logo <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to upload logo <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }


    React.useEffect(() => {
        getClient()
        getAITokensUsed()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    return (
        (<React.Fragment>
            <LoadingBackdropIndeterminate
                open={isLoading}
                text={"Loading Client Configuration"}
            />
            <Box sx={{ pl: 1, pr: 1, width: '100%' }}>
                <Grid container rowSpacing={1} spacing={10} columns={{ xs: 4, sm: 8, lg: 12 }} alignItems={'center'}>
                    <Grid size={4}>
                        <span>Welcome to ProfitMax Pro Suite! As one of our customers, we are here for your business journey. Please feel free to contact us <a href="mailto: NBabcock@cedarrockit.com">by Email</a> with any concerns you may have!</span>
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            margin="dense"
                            id="name"
                            label="Company Name"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={client.company_name || ""}
                            onChange={handleChange("company_name")}
                            sx={{ maxWidth: '300px' }}
                        />
                    </Grid>
                    <Grid alignItems={'center'} container size={4}>
                        <Grid size="auto">
                            {(client?.image_path !== undefined && client?.image_path !== null && client?.image_path !== "") ?
                                <Box component="img" alt={"Company Logo"} src={`/client/${client?.id}/images/client_logo.png?update=${my_user?.client?.date_updated}`} sx={{ maxHeight: '80px' }} />
                                :
                                <Box component="img" alt={"Company Logo"} src={`/noimage.png`} sx={{ maxHeight: '80px' }} />
                            }
                        </Grid>
                        <Grid size="grow">
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                sx={{ marginRight: "1rem" }}
                            >
                                Upload Logo
                                <input type="file" accept="image/png" hidden onChange={uploadNewLogo} />
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container spacing={1} size={4}>
                        <Grid size="grow">
                            <MuiColorInput
                                format="hex"
                                value={companyColor || ""}
                                onChange={(value) => {
                                    setClient({ ...client, client_color: value })
                                    setCompanyColor(value)
                                }}
                                isAlphaHidden
                                margin="dense"
                                fullWidth
                                variant="standard"
                                label="Company Color"
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                        <Grid sx={{ margin: 'auto' }} size="auto">
                            <Button variant="contained" onClick={previewCompanyColor}>
                                Preview
                            </Button>
                        </Grid>
                        <Grid sx={{ margin: 'auto' }} size="auto">
                            <Button disabled={companyColor === previousCompanyColor} variant="contained" color="orange" onClick={resetCompanyColor}>
                                Reset Color
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid size={4}>
                        <span>Active user accounts: {(users ?? []).filter((x) => !x.deactivated).length} / {(client.max_users ?? -1) === -1 ? "∞" : client.max_users}</span>
                    </Grid>
                    {client.has_ai &&
                        <Grid size={4}>
                            <span>AI Tokens used this month: {isLoadingAITokens ? <CircularProgress size="1em" /> : AITokensUsed} / {(client.max_ai_tokens ?? -1) === -1 ? "∞" : client.max_ai_tokens}</span>
                        </Grid>
                    }
                    <Grid size={12}>
                        <Button variant="contained" onClick={save}>
                            Save
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Box>
                    <ClientConfigurationStatuses
                        setClient={setClient}
                        setIsLoading={setIsLoading}
                        service_order_statuses={window.structuredClone(client.service_order_statuses)}
                    />
                </Box>
                <Box>
                    <ClientConfigurationPartsStatuses
                        setClient={setClient}
                        setIsLoading={setIsLoading}
                        service_order_parts_statuses={window.structuredClone(client.service_order_parts_statuses)}
                    />
                </Box>
            </Box>
        </React.Fragment >)
    );
}